<template>
  <ion-page ref="page">
    <LargeNavbar
      show-share-button
      :show-actions="false"
      show-back-button
      :title="'Details'"
      @back-button="onBackButton"
    />
    <ion-content :fullscreen="true">
      <div class="blue-decorative"></div>
      <ion-fab-button class="floating-btn" color="white" @click="router.push({ name: 'Support' })">
        <ion-icon color="primary" :icon="chatboxOutline"></ion-icon>
      </ion-fab-button>
      <div v-if="!isLoading">
        <div v-if="user">
          <SmallCardModal :rental-unit="rentalUnit">
            <template #body>
              <div class="d-flex justify-space-between w-100">
                <SubtitleInformation :rental-unit="rentalUnit" />
                <ReserveBookingModal :rental-unit="rentalUnit" />
              </div>
            </template>
          </SmallCardModal>
        </div>
        <div v-else>
          <SmallCardModal :rental-unit="rentalUnit">
            <template #body>
              <div class="unlock-btn">
                <p class="bold-medium">{{ t('announcement.unlockDetails') }}</p>

                <ion-button
                  @click="goToRegister"
                  fill="solid"
                  expand="block"
                  color="primary"
                  class="text-lg text-weight-bold"
                >
                  {{ t('announcement.createAccount') }}
                </ion-button>
                <a @click="goToLogin" class="bold-medium">{{ t('announcement.orLogin') }}</a>
              </div>
            </template>
          </SmallCardModal>
        </div>
      </div>
      <div
        v-if="isLoading"
        class="PageContainer mt-xs bg-white ion-padding border-default d-flex justify-space-center mt-xs"
        style="padding-right: 10px"
      >
        <div class="content w-100">
          <ion-skeleton-text :animated="true"></ion-skeleton-text>
          <ion-skeleton-text :animated="true"></ion-skeleton-text>
          <SkeletonLoaderCard :width="'100%'" />
          <ion-skeleton-text :animated="true"></ion-skeleton-text>
          <ion-skeleton-text :animated="true"></ion-skeleton-text>
        </div>
      </div>

      <div
        v-if="!isLoading"
        class="PageContainer mt-xs bg-white ion-padding border-default d-flex justify-space-center"
      >
        <div class="content mt-sm w-100">
          <!-- titulo -->
          <h2 class="font-lg blue-darker">{{ getCurrentName(rentalUnit, languageSelected) }}</h2>
          <!-- titulo -->

          <!-- subtitulo -->
          <SubtitleInformation :rental-unit="rentalUnit" />
          <!-- subtitulo -->

          <ion-segment
            style="grid-auto-columns: minmax(auto, 860px)"
            mode="md"
            color="primary"
            v-model="tabSelected"
          >
            <ion-segment-button value="photos">
              <ion-label class="text-weight-bold text-small text-not-capitalize"
                >{{ t('announcement.photos') }}
              </ion-label>
            </ion-segment-button>
            <ion-segment-button
              v-if="rentalUnit.Property.Video && rentalUnit.Property.Video?.url"
              value="videos"
            >
              <ion-label class="text-weight-bold text-small text-not-capitalize"
                >{{ t('announcement.videos') }}
              </ion-label>
            </ion-segment-button>
            <ion-segment-button
              v-if="rentalUnit.Property.VirtualTour && rentalUnit.Property.VirtualTour.url"
              value="virtual"
            >
              <ion-label class="text-weight-bold text-small text-not-capitalize"
                >{{ t('announcement.virtual') }}
              </ion-label>
            </ion-segment-button>
          </ion-segment>
          <div v-if="tabSelected === 'photos'" class="photos-container border-default">
            <MediaViewer :images="getImages()" type-viewer="rental-unit" />
          </div>
          <div v-if="tabSelected === 'videos'" class="photos-container border-default">
            <MediaPreview :url="rentalUnit.Property.Video.url" />
          </div>
          <div v-if="tabSelected === 'virtual'" class="photos-container border-default">
            <MediaPreview :url="rentalUnit.Property.VirtualTour.url" />
          </div>

          <div class="announcement-content ion-padding-top">
            <p v-html="getCurrentDescription(rentalUnit)"></p>

            <CostsAndServices :price="rentalUnit.Prices[0]" />

            <div v-if="rentalUnit.ExtraFeatures.length" class="b-grey"></div>

            <div v-if="rentalUnit.ExtraFeatures.length" class="announcement-content-small">
              <SpacesInformationProperty
                :language="languageSelected"
                :features="rentalUnit.ExtraFeatures"
              />
            </div>

            <div class="b-grey"></div>
            <div class="announcement-content-small">
              <!-- location -->
              <div v-if="rentalUnit.Property && rentalUnit.Property.Location">
                <h2 class="font-md bold-medium">
                  {{ t('announcement.location') }}
                </h2>
                <div>
                  <MapView :height="'80%'" :location="rentalUnit.Property.Location" :zoom="100" />
                </div>

                <ion-row
                  class="ion-justify-content-start ion-align-items-center price-subtitle nowrap"
                >
                  <ion-icon :icon="locationOutline" size="large" color="grey"></ion-icon>
                  <p class="padding-list-item">
                    {{
                      rentalUnit.Property.Location.fullAddress ??
                      rentalUnit.Property.Location.country
                    }}
                  </p>
                </ion-row>
              </div>
              <!-- location -->
            </div>
            <div v-if="rentalUnit.Property.cancellationPolicy" class="b-grey"></div>

            <div v-if="rentalUnit.Property.cancellationPolicy" class="announcement-content-small">
              <RentalConditions :rental-unit="rentalUnit" :language="languageSelected" />
            </div>

            <div class="b-grey"></div>

            <div class="announcement-content-small">
              <LodginForInformation :rental-unit="rentalUnit" />
              <div class="d-flex flex-column gap-xs">
                <div>
                  <h3 class="font-sm bold-medium grey">{{ t('announcement.registry') }}</h3>
                  <ion-row
                    class="ion-justify-content-start ion-align-items-center price-subtitle nowrap"
                  >
                    <p>{{ rentalUnit.referenceCode }}</p>
                  </ion-row>
                </div>
                <div v-if="rentalUnit.Prices[0]">
                  <h3 class="font-sm bold-medium grey">{{ t('announcement.minPeriod') }}</h3>
                  <ion-row
                    class="ion-justify-content-start ion-align-items-center price-subtitle nowrap"
                  >
                    <p>{{ rentalUnit.Prices[0].minPeriod }} {{ t('announcement.days') }}</p>
                  </ion-row>
                </div>
              </div>
            </div>

            <div class="b-grey"></div>

            <div class="announcement-content-small">
              <ion-row class="ion-justify-content-start price-subtitle nowrap">
                <ion-icon
                  class="medium-icon"
                  :icon="bedOutline"
                  size="large"
                  color="grey"
                ></ion-icon>
                <div class="padding-list-item">
                  <h3 class="font-sm bold-medium grey no-margin-top">
                    {{ t('announcement.roomIn') }}
                  </h3>
                  <p class="list-item-text">
                    {{ t('announcement.yourOwn') }}
                  </p>
                </div>
              </ion-row>
              <ion-row class="ion-justify-content-start price-subtitle nowrap">
                <ion-icon :icon="homeOutline" size="large" color="grey"></ion-icon>
                <div class="padding-list-item">
                  <h3 class="font-sm bold-medium grey no-margin-top">
                    {{ t('announcement.sharedSpaces') }}
                  </h3>
                  <p class="list-item-text">{{ t('announcement.youWill') }}</p>
                </div>
              </ion-row>
              <ion-row class="ion-justify-content-start price-subtitle nowrap">
                <ion-icon :icon="locationOutline" size="large" color="grey"></ion-icon>
                <div class="padding-list-item">
                  <h3 class="font-sm bold-medium grey no-margin-top">
                    {{ t('announcement.sharedBathroom') }}
                  </h3>
                  <p class="list-item-text">{{ t('announcement.youWillShare') }}</p>
                </div>
              </ion-row>
            </div>

            <div
              v-if="rentalUnit.Images && rentalUnit.Images[0] && user"
              class="announcement-content-small"
            >
              <!--               <div>
                <h1 class="font-md bold-medium ion-padding-bottom">
                  {{ t('announcement.youWillStay') }}
                </h1>
                <img
                  class="principal-image border-default"
                  :src="rentalUnit.Images[0].image"
                  alt=""
                />
              </div> -->
              <div v-if="rentalUnit.bedType">
                <h3 class="font-sm bold-medium grey">{{ t('announcement.bedroom') }}</h3>
                <p class="list-item-text">{{ rentalUnit.bedType }}</p>
              </div>
            </div>

            <div class="b-grey"></div>

            <div v-if="rentalUnit.notes && user" class="announcement-content-small">
              <div>
                <h1 class="font-md bold-medium">{{ t('announcement.services') }}</h1>
                <p v-html="rentalUnit.notes"></p>
              </div>
            </div>
          </div>

          <div v-if="mergedData && !internalGuideLoading" class="mt-lg">
            <p class="font-md bold-medium mb-xs">{{ t('post.guides') }}</p>
            <div class="d-flex post-content-list gap-xs mt-lg">
              <div v-for="post in getFilteredPosts" :key="post.id">
                <PostCard :post="post" />
              </div>
            </div>
          </div>
          <div v-else class="d-flex flex-column gap-xs mt-xs">
            <SkeletonLoaderCard />
          </div>
        </div>
      </div>
    </ion-content>
  </ion-page>
</template>

<script setup lang="ts">
  import { computed, ref } from 'vue';
  import { useElasticStore } from '@/stores/elastic-store';
  import {
    IonLabel,
    IonSegmentButton,
    IonSegment,
    IonRow,
    IonPage,
    IonIcon,
    IonContent,
    IonSkeletonText,
    IonButton,
    IonFabButton,
  } from '@ionic/vue';
  import router from '@/router';
  import { useRoute } from 'vue-router';
  import { locationOutline, bedOutline, homeOutline, chatboxOutline } from 'ionicons/icons';
  import LargeNavbar from '@/components/LargeNavbar.vue';
  import MediaViewer from '@/components/announcement/MediaViewer.vue';
  import MediaPreview from '@/components/announcement/MediaPreview.vue';
  import LodginForInformation from '@/components/announcement/LodginForInformation.vue';
  import RentalConditions from '@/components/announcement/RentalConditions.vue';
  import SmallCardModal from '@/components/announcement/SmallCardModal.vue';
  import SubtitleInformation from '@/components/announcement/SubtitleInformation.vue';
  import MapView from '@/components/announcement/MapView.vue';
  import { useAuthStore } from '@/stores/auth-store';
  import { useSearch } from '@/composables/useSearch';
  import { RentalUnit } from '@/interfaces/elastic/elastic.interface';
  import { useI18n } from 'vue-i18n';
  import SkeletonLoaderCard from '@/components/loaders/SkeletonLoaderCard.vue';
  import ReserveBookingModal from '@/components/announcement/ReserveBookingModal.vue';
  import { storeToRefs } from 'pinia';
  import { useWebflowStore } from '@/stores/webflow-store';
  import { useWebflow } from '@/composables/useWebflow';
  import PostCard from '@/components/posts/PostCard.vue';
  import { ProfileStore, useProfileStore } from '@/stores/profile-store';
  import SpacesInformationProperty from '@/components/announcement/SpacesInformationProperty.vue';
  import CostsAndServices from '@/components/announcement/CostsAndServices.vue';

  const { t } = useI18n();
  const isLoading = ref(true);

  const { getRentalUnitPreview, resetStore } = useElasticStore();
  const rentalUnit = ref();
  const { getCurrentName } = useSearch();
  const { user } = storeToRefs(useAuthStore());
  const { mergedData, todayPosts } = storeToRefs(useWebflowStore());
  const { internalGuideLoading, handleGetCollections, getFilteredCategoriesPosts, getCategory } =
    useWebflow();
  const profileStore: ProfileStore = useProfileStore();
  const { languageSelected } = storeToRefs(profileStore);

  const tabSelected = ref('photos');
  const route = useRoute();

  const getFilteredPosts = computed(() => {
    const posts = getFilteredCategoriesPosts(
      getCategory(),
      rentalUnit.value.Property.Location?.country ??
        (rentalUnit.value.Property.Location?.city as string),
    );
    return posts.length > 0 ? posts : todayPosts.value;
  });

  const onBackButton = () => {
    router.back();
  };

  const goToRegister = () => {
    resetStore();
    router.push({ name: 'signUp', query: { rentalId: route.params.rentalId as string } });
  };

  const goToLogin = () => {
    resetStore();
    router.push({ name: 'login', query: { rentalId: route.params.rentalId as string } });
  };

  const getImages = () => {
    if (rentalUnit?.value) {
      return rentalUnit?.value.Property.Images.concat(rentalUnit?.value.Images);
    }
    return [];
  };

  const initialConfig = async () => {
    try {
      isLoading.value = true;

      const data = await getRentalUnitPreview(route.params.rentalId as string);
      setMetaData();
      await handleGetCollections();

      if (data) {
        rentalUnit.value = data;
      }
    } catch (error) {
      console.error('error', error);
    } finally {
      isLoading.value = false;
    }
  };

  const getCurrentDescription = (rentalObj?: RentalUnit) => {
    if (rentalObj && rentalObj.Texts) {
      return rentalObj.Texts[
        `description_${languageSelected.value}` as keyof typeof rentalObj.Texts
      ];
    }
    return rentalObj?.description ?? '';
  };

  const setMetaData = () => {
    if (rentalUnit.value) {
      document.title = getCurrentName(rentalUnit.value, languageSelected.value) as string;

      const ogTitle = document.createElement('meta');
      ogTitle.setAttribute('property', 'og:title');
      ogTitle.content = getCurrentName(rentalUnit.value, languageSelected.value) as string;
      document.getElementsByTagName('head')[0].appendChild(ogTitle);
    }
  };

  initialConfig();
</script>

<style lang="css" scoped></style>
